import { cn } from "@tcg/ui";
import { Check, Minus, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

import { MoneyText } from "@/components/MoneyText";
import { ItemBadges } from "@/features/sale/ItemBadges";
import type { ReturnableLine } from "@/lib/types";

export const RETURN_REASONS = ["changed_mind", "damaged", "wrong_item", "other"] as const;
export type ReturnReason = (typeof RETURN_REASONS)[number];
export type Inspection = "restock" | "damaged";

export interface LineChoice {
  qty: number;
  reason: ReturnReason;
  inspection: Inspection;
}

/**
 * Which items come back (design/05 §3.6): a big tick per line (qty for a sealed box bought
 * several times), why (お客様都合 / 商品不良 / 誤会計 / その他) and what happens to the card
 * (再販可 = back on sale · 破損 = written off) with one line saying so.
 */
export function ReturnLines({
  lines,
  chosen,
  onChange,
}: {
  lines: ReturnableLine[];
  chosen: ReadonlyMap<string, LineChoice>;
  onChange: (lineId: string, choice: LineChoice | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage === "ja" ? "ja" : "en";
  return (
    <ul className="flex flex-col gap-2">
      {lines.map((line) => {
        const choice = chosen.get(line.lineId);
        const gone = line.returnableQty <= 0;
        const title = line.title[lang] ?? line.title.ja ?? line.title.en ?? "";
        return (
          <li
            key={line.lineId}
            data-testid="return-line"
            className={cn(
              "flex flex-col gap-2 rounded-md border p-3",
              choice && "border-primary bg-accent/40",
              gone && "opacity-60",
            )}
          >
            <div className="flex items-center gap-3">
              <button
                type="button"
                role="checkbox"
                aria-checked={Boolean(choice)}
                aria-label={t("returns.pick_line", { title })}
                disabled={gone}
                onClick={() =>
                  onChange(
                    line.lineId,
                    choice ? null : { qty: 1, reason: "changed_mind", inspection: "restock" },
                  )
                }
                className="flex size-12 shrink-0 items-center justify-center rounded-md border-2 bg-background aria-checked:border-primary aria-checked:bg-primary aria-checked:text-primary-foreground"
              >
                {choice ? <Check aria-hidden className="size-7" /> : null}
              </button>
              <div className="min-w-0 flex-1">
                <p className="truncate text-lg font-medium">{title}</p>
                <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                  <ItemBadges
                    productType={line.productType}
                    rawRank={line.rawRank}
                    grading={line.grading}
                    size="sm"
                  />
                  {line.sku ? <span>{t("returns.code", { code: line.sku })}</span> : null}
                  {line.qty > 1 ? (
                    <span>{t("returns.bought_qty", { count: line.qty })}</span>
                  ) : null}
                  {line.returnedQty > 0 ? (
                    <span>{t("returns.already_back", { count: line.returnedQty })}</span>
                  ) : null}
                  {line.finalSale ? (
                    <span className="font-medium text-pos-problem">
                      {t("returns.final_sale_badge")}
                    </span>
                  ) : null}
                </p>
              </div>
              <MoneyText amount={line.lineTotal} size="lg" />
            </div>
            {choice ? (
              <div className="grid gap-2 pl-15 md:grid-cols-[auto_1fr_1fr]">
                {line.returnableQty > 1 ? (
                  <span
                    className="flex items-center gap-1"
                    role="group"
                    aria-label={t("returns.qty")}
                  >
                    <button
                      type="button"
                      aria-label={t("returns.qty_minus")}
                      disabled={choice.qty <= 1}
                      onClick={() => onChange(line.lineId, { ...choice, qty: choice.qty - 1 })}
                      className="flex size-12 items-center justify-center rounded-md border disabled:opacity-40"
                    >
                      <Minus aria-hidden />
                    </button>
                    <output className="w-10 text-center text-xl font-semibold tabular-nums">
                      {choice.qty}
                    </output>
                    <button
                      type="button"
                      aria-label={t("returns.qty_plus")}
                      disabled={choice.qty >= line.returnableQty}
                      onClick={() => onChange(line.lineId, { ...choice, qty: choice.qty + 1 })}
                      className="flex size-12 items-center justify-center rounded-md border disabled:opacity-40"
                    >
                      <Plus aria-hidden />
                    </button>
                  </span>
                ) : (
                  <span />
                )}
                <fieldset className="flex flex-wrap gap-1">
                  <legend className="sr-only">{t("returns.reason")}</legend>
                  {RETURN_REASONS.map((reason) => (
                    <button
                      key={reason}
                      type="button"
                      aria-pressed={choice.reason === reason}
                      onClick={() => onChange(line.lineId, { ...choice, reason })}
                      className="min-h-12 rounded-md border px-3 aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground"
                    >
                      {t(`returns.reason_${reason}`)}
                    </button>
                  ))}
                </fieldset>
                <fieldset className="flex flex-col gap-1">
                  <legend className="sr-only">{t("returns.inspection")}</legend>
                  <div className="flex gap-1">
                    {(["restock", "damaged"] as const).map((inspection) => (
                      <button
                        key={inspection}
                        type="button"
                        aria-pressed={choice.inspection === inspection}
                        onClick={() => onChange(line.lineId, { ...choice, inspection })}
                        className="min-h-12 flex-1 rounded-md border px-3 aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground"
                      >
                        {t(`returns.inspection_${inspection}`)}
                      </button>
                    ))}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {t(`returns.inspection_${choice.inspection}_help`)}
                  </span>
                </fieldset>
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
